import { useEffect, useMemo, useRef, useState, type PointerEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../../components/FeatherIcon';
import { LoadingBlock } from '../../components/Spinner';
import type { CrmAccount } from '../../crm/crmAccess';
import type { SearchHit } from '../api';
import type { AccountColour } from '../colour';
import { useFanSearch } from '../useFanSearch';
import { Avatar, FanAvatar } from './Avatar';
import { frameStyle, type FrameState } from './frame';

const RECENT_KEY = 'om_ws_recent_searches';
/** Keeps browsers and password managers from treating the box as a login field. */
export const NO_AUTOFILL = {
  type: 'text',
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
} as const;
const RECENT_MAX = 5;

export interface RecentSearch {
  account_id: string;
  fan_id: string;
  label: string;
  fan_name?: string;
  fan_avatar?: string | null;
}

function readRecent(): RecentSearch[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]') as unknown;
    return Array.isArray(raw) ? (raw as RecentSearch[]).filter((r) => r && r.account_id && r.fan_id).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

interface Props {
  frame: FrameState;
  accounts: CrmAccount[];
  inputRef: RefObject<HTMLInputElement | null>;
  accountId: string | null;
  colourOf: (accountId: string) => AccountColour;
  onAccount: (id: string) => void;
  onPick: (hit: { account_id: string; fan_id: string; fan_name: string; fan_username?: string | null; fan_avatar?: string | null }) => void;
  /** Pointer pressed on a result: dragging it onto Pinned pins the chat. */
  onDragStart: (hit: SearchHit, e: PointerEvent) => void;
  onRemove: () => void;
  onMoveStart: (e: PointerEvent) => void;
  onResizeStart: (e: PointerEvent) => void;
}

export function SearchWidget({ frame, accounts, inputRef, accountId, colourOf, onAccount, onPick, onDragStart, onRemove, onMoveStart, onResizeStart }: Props) {
  const { t } = useTranslation('workspace');
  const [q, setQ] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [accountQuery, setAccountQuery] = useState('');
  const [recent, setRecent] = useState<RecentSearch[]>(readRecent);
  const account = accounts.find((a) => a.account_id === accountId) || null;
  const query = q.trim();
  const { hits, busy, error } = useFanSearch(accountId, query);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const close = (e: MouseEvent) => !wrap.current?.contains(e.target as Node) && setPickerOpen(false);
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [pickerOpen]);

  const remember = (hit: SearchHit) => {
    const label = hit.fan_username ? `@${hit.fan_username}` : hit.fan_name;
    const next = [{ account_id: hit.account_id, fan_id: hit.fan_id, label, fan_name: hit.fan_name, fan_avatar: hit.fan_avatar }, ...recent.filter((r) => !(r.account_id === hit.account_id && r.fan_id === hit.fan_id))].slice(0, RECENT_MAX);
    setRecent(next);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable */
    }
  };

  const shownAccounts = useMemo(() => {
    const f = accountQuery.trim().toLowerCase();
    return accounts.filter((a) => !f || `${a.profile_name} ${a.username}`.toLowerCase().includes(f));
  }, [accounts, accountQuery]);

  const recentShown = recent.filter((r) => accounts.some((a) => a.account_id === r.account_id));
  const nameOf = (a: CrmAccount | undefined | null) => a?.profile_name || a?.username || '';

  return (
    <div ref={wrap} className={`ws-search ${frame.editing ? 'is-editing' : ''}`} style={{ ...frameStyle({ ...frame, raised: frame.raised || !!query || pickerOpen }) }}>
      {frame.editing && (
        <span className="ws-search-grip" onPointerDown={onMoveStart} title={t('widget.move')}>
          <FeatherIcon name="grip-vertical" size={15} />
        </span>
      )}
      <button type="button" className="ws-search-account" onClick={() => setPickerOpen((v) => !v)}>
        {account && <Avatar src={account.avatar_url} name={nameOf(account)} size={16} ring={`0 0 0 1.5px ${colourOf(account.account_id).accent}`} />}
        <span className="ws-search-account-name">{account ? nameOf(account) : t('search.pickAccount')}</span>
        <FeatherIcon name="chevron-down" size={12} />
      </button>
      <FeatherIcon name="search" size={14} className="ws-search-ic" />
      <input
        {...NO_AUTOFILL}
        name="ws-fan-search"
        role="searchbox"
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setQ('');
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Enter' && hits[0]) {
            remember(hits[0]);
            onPick(hits[0]);
            setQ('');
          }
        }}
        placeholder={account ? t('search.placeholder', { model: nameOf(account) }) : t('search.placeholderNoAccount')}
      />
      {!query && (
        <span className="ws-search-recent">
          <span className="ws-kbd">/</span>
          {recentShown.length > 0 && <span className="ws-search-recent-label">{t('search.recent')}</span>}
          {recentShown.map((r) => {
            const a = accounts.find((x) => x.account_id === r.account_id);
            return (
              <button
                key={`${r.account_id}:${r.fan_id}`}
                type="button"
                className="ws-pill ws-pill--btn"
                onClick={() => onPick({ account_id: r.account_id, fan_id: r.fan_id, fan_name: r.fan_name || r.label, fan_avatar: r.fan_avatar ?? null })}
              >
                {r.fan_avatar ? (
                  <FanAvatar accountId={r.account_id} src={r.fan_avatar} name={r.fan_name || r.label} size={16} ring={`0 0 0 1.5px ${colourOf(r.account_id).accent}`} />
                ) : (
                  <Avatar src={a?.avatar_url} name={nameOf(a)} size={16} ring={`0 0 0 1.5px ${colourOf(r.account_id).accent}`} />
                )}
                {r.label}
              </button>
            );
          })}
        </span>
      )}
      {frame.editing && (
        <>
          <button type="button" className="ws-icon-btn" title={t('widget.remove')} onClick={onRemove}>
            <FeatherIcon name="x" size={14} />
          </button>
          <span className="ws-search-resize" onPointerDown={onResizeStart} title={t('widget.resize')} />
        </>
      )}
      {pickerOpen && (
        <div className="ws-menu ws-search-picker">
          <input {...NO_AUTOFILL} name="ws-account-filter" autoFocus value={accountQuery} onChange={(e) => setAccountQuery(e.target.value)} placeholder={t('search.findAccount')} />
          <div className="ws-menu-scroll">
            {shownAccounts.map((a) => (
              <button
                key={a.account_id}
                type="button"
                className={`ws-menu-item ${a.account_id === accountId ? 'is-on' : ''}`}
                onClick={() => {
                  onAccount(a.account_id);
                  setPickerOpen(false);
                  inputRef.current?.focus();
                }}
              >
                <Avatar src={a.avatar_url} name={nameOf(a)} size={20} ring={`0 0 0 1.5px ${colourOf(a.account_id).accent}`} />
                <span className="ws-menu-label">{nameOf(a)}</span>
                <span className="ws-muted">@{a.username}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {query && (
        <div className="ws-menu ws-search-results">
          {busy && !hits.length && <LoadingBlock pad="14px 0" size={18} label={t('search.searching')} />}
          {error && <span className="ws-menu-note ws-error">{error}</span>}
          {hits.map((h) => {
            const c = colourOf(h.account_id);
            return (
              <button
                key={h.fan_id}
                type="button"
                className="ws-row ws-row--result"
                onPointerDown={(e) => onDragStart(h, e)}
                onClick={() => {
                  remember(h);
                  onPick(h);
                  setQ('');
                }}
              >
                <span className="ws-row-bar" style={{ background: c.accent }} />
                <FanAvatar accountId={h.account_id} src={h.fan_avatar} name={h.fan_name} size={28} />
                <span className="ws-row-main">
                  <span className="ws-row-line">
                    <Avatar src={account?.avatar_url} name={nameOf(account)} size={16} ring={`0 0 0 1.5px ${c.accent}`} />
                    <span className="ws-row-model" style={{ color: c.accent }}>
                      {nameOf(account)}
                    </span>
                    <span className="ws-flex" />
                    {h.spent != null && <span className="ws-row-spent">${h.spent.toLocaleString('en-US')}</span>}
                  </span>
                  <span className="ws-row-line ws-row-line--fan">
                    <span className="ws-row-fan">{h.fan_name}</span>
                    {h.fan_username && <span className="ws-muted">@{h.fan_username}</span>}
                  </span>
                  {h.snippet && <span className="ws-row-sub ws-tone-muted">{h.snippet}</span>}
                </span>
              </button>
            );
          })}
          {!busy && !error && !hits.length && <span className="ws-menu-note">{t('search.noResults', { q: query })}</span>}
        </div>
      )}
    </div>
  );
}
